/* THE LANGUAGE PICKER — a globe that opens the list (Max, 2026-10-21, with a picker of the kind docs sites carry:
   "đổi thành quả địa cầu và thêm ngôn ngữ tiếng Việt và tiếng Trung giản thể"). With two languages the header
   carried one button naming the language it switched TO; with four, one click cannot say which, so the button is
   the globe and the choice is a list: each language in its own name, its country code beside it, a tick on the
   one in use. Purely client-side — the dictionaries ship with the bundle, so choosing makes no request.
   Closes on a choice, a click elsewhere and Escape (which hands the focus back to the globe). */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Globe } from 'lucide-react';
import { LANGUAGES, setPortalLanguage } from './i18n';

export default function LanguagePicker() {
    const { t, i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const btnRef = useRef(null);
    const current = LANGUAGES.find((l) => String(i18n.language || '').startsWith(l.code)) || LANGUAGES[0];

    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => {
            if (e.key === 'Escape') { setOpen(false); btnRef.current?.focus(); }
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        // The list takes the focus on its own entry, so a keyboard opens it and arrows through it.
        const lit = wrapRef.current?.querySelector('.lang-opt[aria-checked="true"]');
        lit?.focus();
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const onListKey = (e) => {
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        e.preventDefault();
        const opts = [...(wrapRef.current?.querySelectorAll('.lang-opt') || [])];
        const at = opts.indexOf(document.activeElement);
        const next = opts[(at + (e.key === 'ArrowDown' ? 1 : opts.length - 1)) % opts.length];
        next?.focus();
    };

    const choose = (code) => {
        setPortalLanguage(code);
        setOpen(false);
        btnRef.current?.focus();
    };

    return (
        <div className="lang-pick" ref={wrapRef}>
            <button
                ref={btnRef}
                type="button"
                className="lang-globe"
                title={`${t('common.language')}: ${current.label}`}
                aria-label={`${t('common.language')}: ${current.label}`}
                aria-haspopup="menu"
                aria-expanded={open}
                data-testid="lang-globe"
                onClick={() => setOpen((v) => !v)}
            >
                <Globe size={16} />
            </button>
            {open && (
                <div className="lang-menu" role="menu" aria-label={t('common.language')} onKeyDown={onListKey} data-testid="lang-menu">
                    {LANGUAGES.map((l) => {
                        const on = l.code === current.code;
                        return (
                            <button
                                key={l.code}
                                type="button"
                                role="menuitemradio"
                                aria-checked={on}
                                lang={l.htmlLang}
                                className={`lang-opt${on ? ' on' : ''}`}
                                data-lang={l.code}
                                onClick={() => choose(l.code)}
                            >
                                <span className="lang-tick" aria-hidden="true">{on && <Check size={14} strokeWidth={2.6} />}</span>
                                <span className="lang-flag">{l.flag}</span>
                                <span className="lang-name">{l.label}</span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
